import { adminClient } from "@/lib/api";

// Files stay in the user's current timeline. Retry a failed clip without
// uploading earlier completed clips again; never put all bytes in one request.
const savedFiles = new WeakMap();
let pendingRequest = null;
const pause = ms => new Promise(resolve => setTimeout(resolve, ms));

export const videoStudioError = error => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const prefix = status ? `HTTP ${status}: ` : "";
    if (status === 401 || status === 403) return "Your admin session ended. Sign in again; your saved clips remain on the server.";
    if (typeof detail === "string") return prefix + detail;
    if (Array.isArray(detail)) return prefix + detail.map(item => item.msg).filter(Boolean).join("; ");
    if (status === 413) return "HTTP 413: this individual clip exceeded the upload limit. Maximum 80MB per video or 8MB per photo.";
    if (status === 404) return "HTTP 404: the Video Studio upload endpoint or saved clip is unavailable.";
    if (status >= 500) return prefix + "The server was temporarily unavailable. Retry; completed clips are kept.";
    if (error?.code === "ECONNABORTED" || /network|timeout/i.test(error?.message || "")) return "The connection was interrupted. Retry; completed clips will not be uploaded again.";
    return error?.message || "The request could not be completed. Please retry.";
};

async function savedClip(file, kind, report) {
    let reference = savedFiles.get(file);
    if (reference) {
        try {
            const { data } = await adminClient().get(`/admin/video-uploads/${reference.id}`, { timeout: 20000 });
            report(file.size);
            return data.id;
        } catch (error) {
            if (error?.response?.status === 401 || error?.response?.status === 403) throw error;
            if (error?.response?.status === 404) reference = null;
        }
    }
    if (!reference) {
        reference = { id: crypto.randomUUID().replaceAll("-", "") };
        savedFiles.set(file, reference);
    }
    for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
            if (attempt > 0) {
                // A response may have been lost AFTER the server saved the
                // clip. Look it up before sending those bytes a second time.
                try {
                    const { data } = await adminClient().get(`/admin/video-uploads/${reference.id}`, { timeout: 20000 });
                    report(file.size);
                    return data.id;
                } catch (error) { if ([401, 403].includes(error?.response?.status)) throw error; }
            }
            const form = new FormData();
            form.append("kind", kind); form.append("file", file);
            const { data } = await adminClient().post(`/admin/video-uploads/${reference.id}`, form, {
                timeout: 15 * 60 * 1000,
                onUploadProgress: event => report(Math.min(file.size, event.loaded)),
            });
            report(file.size);
            return data.id;
        } catch (error) {
            const status = error?.response?.status;
            if (attempt === 2 || (status && status < 500 && ![408, 429].includes(status))) throw error;
            await pause(1000 * (attempt + 1));
        }
    }
}

export async function uploadVideoTimeline(clips, globalCaption, onProgress) {
    const totalBytes = clips.reduce((sum, clip) => sum + clip.file.size, 0);
    let completeBytes = 0;
    const timeline = [];
    for (let index = 0; index < clips.length; index += 1) {
        const clip = clips[index];
        let id;
        try {
            id = await savedClip(clip.file, clip.kind, loaded => onProgress({
                phase: loaded >= clip.file.size ? `Saving clip ${index + 1} of ${clips.length}…` : `Uploading clip ${index + 1} of ${clips.length}…`,
                current: index, total: clips.length,
                bytes: completeBytes + loaded, totalBytes,
            }));
        } catch (error) {
            const wrapped = new Error(`Clip ${index + 1} (${clip.file.name}): ${videoStudioError(error)}`);
            wrapped.cause = error;
            throw wrapped;
        }
        completeBytes += clip.file.size;
        timeline.push({ kind: clip.kind, upload_id: id, text: clip.caption.text,
            family: clip.caption.family, size: clip.caption.size, sizePx: clip.caption.sizePx || null,
            position: clip.caption.position, fit: clip.fit, motion: clip.motion,
            duration: clip.duration || 2.5, start_sec: clip.startSec || 0, end_sec: clip.endSec || 0,
            keep_audio: clip.keepAudio !== false, enhance: !!clip.enhance });
    }
    const caption = globalCaption.text.trim() ? globalCaption : null;
    const signature = JSON.stringify([timeline, caption]);
    if (!pendingRequest || pendingRequest.signature !== signature) pendingRequest = { signature, id: crypto.randomUUID().replaceAll("-", "") };
    const form = new FormData();
    form.append("clips_json", JSON.stringify(timeline));
    form.append("global_caption_json", JSON.stringify(caption));
    form.append("request_id", pendingRequest.id);
    onProgress({ phase: "All clips saved. Starting render…", current: clips.length, total: clips.length, bytes: totalBytes, totalBytes });
    const { data } = await adminClient().post("/admin/videos", form, { timeout: 120000 });
    if (!data?.id) throw new Error("The server did not return a render ID. Retry to check the same request.");
    pendingRequest = null;
    return data.id;
}